import { snakeCase } from 'lodash-es'

/**
 * A TypeScript utility type that represents the snake_case version of a given string type.
 *
 * @template S - The input string type.
 */
export type SnakeCase<S extends string> = S extends `${infer T}${infer U}`
  ? U extends Uncapitalize<U>
    ? `${T}${SnakeCase<U>}`
    : `${T}_${SnakeCase<Uncapitalize<U>>}`
  : S

/**
 * A TypeScript utility type that maps the keys of an object to snake_case query parameters.
 *
 * @template T - The object type to be converted.
 * @template Q - The query type ('filter' or 'page').
 */
export type FilterQuery<
  T extends Record<string, any>,
  Q extends 'filter' | 'page'
> = {
  [K in keyof T as `${Q}[${SnakeCase<K & string>}]`]: T[K]
}

/**
 * Converts an object into a query parameter format where the keys follow snake_case convention.
 *
 * 🔹 The transformation of keys happens at **runtime**, using a `snakeCase` function.
 *
 * @template T - The type of the input object.
 * @template Q - The query type ('filter' or 'page').
 *
 * @param {T} filters - The object containing key-value pairs to be converted.
 * @param {Q} queryType - The prefix for the query parameter keys ('filter' or 'page').
 * @returns {FilterQuery<T, Q>} An object with transformed keys in snake_case format <filter | page>[field_in_snake].
 */
export const toQueryParams = <
  T extends Record<string, any>,
  Q extends 'filter' | 'page'
>(
  filters: T,
  queryType: Q
): FilterQuery<T, Q> => {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[`${queryType}[${snakeCase(key)}]`] = value
    }
    return acc
  }, {} as FilterQuery<T, Q>)
}

export const toFilterQueryParams = <T extends Record<string, any>>(
  filter: T
) => {
  return toQueryParams(filter, 'filter') as FilterQuery<T, 'filter'>
}

export const toPaginationQueryParams = <T extends Record<string, any>>(
  pagination: T
) => {
  return toQueryParams(pagination, 'page') as FilterQuery<T, 'page'>
}
